import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'

export type TestCriterion =
  | { type: 'LLM_JUDGE'; text: string }
  | { type: 'ACTION_EXPECTED'; toolId: string }
  | { type: 'ACTION_FORBIDDEN'; toolId: string }

export type AgentTestScenario = {
  id: string
  organizationId: string
  agentId: string
  name: string
  persona: string
  objective: string | null
  criteria: TestCriterion[]
  maxTurns: number
  enabled: boolean
  source: 'MANUAL' | 'AI_SUGGESTED'
  createdAt: string
  updatedAt: string
}

export type SuggestedScenario = {
  name: string
  persona: string
  objective: string | null
  maxTurns: number
  criteria: TestCriterion[]
}

export type AgentTestRunStatus =
  | 'PENDING'
  | 'RUNNING'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED'

export type AgentTestCaseStatus =
  | 'PENDING'
  | 'RUNNING'
  | 'PASSED'
  | 'FAILED'
  | 'ERROR'
  | 'SKIPPED'

export type TranscriptEntry = {
  role: 'lead' | 'agent'
  content: string
  actions?: Array<{ type: string } & Record<string, unknown>>
}

export type CriterionVerdict = {
  criterionIndex: number
  type: TestCriterion['type']
  criterion: string
  passed: boolean
  reasoning: string
  evidence: string
}

export type AgentTestCaseResult = {
  id: string
  runId: string
  scenarioId: string | null
  scenarioName: string
  personaSnapshot: string
  criteriaSnapshot: TestCriterion[]
  status: AgentTestCaseStatus
  errorMessage: string | null
  transcript: TranscriptEntry[]
  actions: Array<{
    turn: number
    toolId: string
    action: Record<string, unknown>
  }>
  turns: number
  stopReason: string | null
  verdicts: CriterionVerdict[]
  passed: boolean | null
  judgeNotes: string | null
}

export type AgentTestRun = {
  id: string
  agentId: string
  status: AgentTestRunStatus
  errorMessage: string | null
  instructionsHash: string
  promptVersionId: string | null
  simulatorModel: string
  judgeModel: string
  totalScenarios: number
  completedScenarios: number
  passedScenarios: number
  failedScenarios: number
  inputTokens: number
  outputTokens: number
  costUsd: string
  createdAt: string
  startedAt: string | null
  completedAt: string | null
  results?: AgentTestCaseResult[]
}

export type TestQuota = {
  canRun: boolean
  blockedReason: string | null
  activeRunId: string | null
  usedThisMonth: number
  monthlyCap: number
  enabledScenarios: number
  maxScenariosPerRun: number
}

export type ScenarioInput = {
  name: string
  persona: string
  objective?: string | null
  criteria: TestCriterion[]
  maxTurns?: number
  enabled?: boolean
  source?: 'MANUAL' | 'AI_SUGGESTED'
}

const testKeys = {
  scenarios: (orgId: string | null, agentId: string | null) =>
    ['agent-tests', 'scenarios', orgId, agentId] as const,
  runs: (orgId: string | null, agentId: string | null) =>
    ['agent-tests', 'runs', orgId, agentId] as const,
  run: (orgId: string | null, agentId: string | null, runId: string | null) =>
    ['agent-tests', 'run', orgId, agentId, runId] as const,
  quota: (orgId: string | null, agentId: string | null) =>
    ['agent-tests', 'quota', orgId, agentId] as const,
}

const ACTIVE_RUN_STATUSES: AgentTestRunStatus[] = ['PENDING', 'RUNNING']

export function useTestScenarios(orgId: string | null, agentId: string | null) {
  return useQuery<AgentTestScenario[]>({
    queryKey: testKeys.scenarios(orgId, agentId),
    queryFn: async () => {
      const res = await api.get<AgentTestScenario[]>(
        `/orgs/${orgId}/agents/${agentId}/testing/scenarios`
      )
      return res.data
    },
    enabled: !!orgId && !!agentId,
    staleTime: 30_000,
  })
}

export function useCreateScenario(
  orgId: string | null,
  agentId: string | null
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: ScenarioInput) => {
      const res = await api.post<AgentTestScenario>(
        `/orgs/${orgId}/agents/${agentId}/testing/scenarios`,
        body
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: testKeys.scenarios(orgId, agentId),
      })
      void qc.invalidateQueries({ queryKey: testKeys.quota(orgId, agentId) })
    },
  })
}

export function useUpdateScenario(
  orgId: string | null,
  agentId: string | null
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      scenarioId,
      ...body
    }: Partial<ScenarioInput> & { scenarioId: string }) => {
      const res = await api.put<AgentTestScenario>(
        `/orgs/${orgId}/agents/${agentId}/testing/scenarios/${scenarioId}`,
        body
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: testKeys.scenarios(orgId, agentId),
      })
      void qc.invalidateQueries({ queryKey: testKeys.quota(orgId, agentId) })
    },
  })
}

export function useDeleteScenario(
  orgId: string | null,
  agentId: string | null
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (scenarioId: string) => {
      const res = await api.delete(
        `/orgs/${orgId}/agents/${agentId}/testing/scenarios/${scenarioId}`
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: testKeys.scenarios(orgId, agentId),
      })
      void qc.invalidateQueries({ queryKey: testKeys.quota(orgId, agentId) })
    },
  })
}

export function useSuggestScenarios(
  orgId: string | null,
  agentId: string | null
) {
  return useMutation({
    mutationFn: async (body: { count?: number } = {}) => {
      const res = await api.post<SuggestedScenario[]>(
        `/orgs/${orgId}/agents/${agentId}/testing/scenarios/suggest`,
        body
      )
      return res.data
    },
  })
}

export function useTestQuota(orgId: string | null, agentId: string | null) {
  return useQuery<TestQuota>({
    queryKey: testKeys.quota(orgId, agentId),
    queryFn: async () => {
      const res = await api.get<TestQuota>(
        `/orgs/${orgId}/agents/${agentId}/testing/quota`
      )
      return res.data
    },
    enabled: !!orgId && !!agentId,
    staleTime: 15_000,
  })
}

export function useTestRuns(orgId: string | null, agentId: string | null) {
  return useQuery<AgentTestRun[]>({
    queryKey: testKeys.runs(orgId, agentId),
    queryFn: async () => {
      const res = await api.get<AgentTestRun[]>(
        `/orgs/${orgId}/agents/${agentId}/testing/runs`
      )
      return res.data
    },
    enabled: !!orgId && !!agentId,
    staleTime: 10_000,
    // Enquanto houver execução ativa, acompanha via polling.
    refetchInterval: (query) =>
      query.state.data?.some((run) => ACTIVE_RUN_STATUSES.includes(run.status))
        ? 4_000
        : false,
  })
}

export function useTestRun(
  orgId: string | null,
  agentId: string | null,
  runId: string | null
) {
  return useQuery<AgentTestRun>({
    queryKey: testKeys.run(orgId, agentId, runId),
    queryFn: async () => {
      const res = await api.get<AgentTestRun>(
        `/orgs/${orgId}/agents/${agentId}/testing/runs/${runId}`
      )
      return res.data
    },
    enabled: !!orgId && !!agentId && !!runId,
    refetchInterval: (query) =>
      query.state.data && ACTIVE_RUN_STATUSES.includes(query.state.data.status)
        ? 4_000
        : false,
  })
}

export function useStartTestRun(orgId: string | null, agentId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const res = await api.post<AgentTestRun>(
        `/orgs/${orgId}/agents/${agentId}/testing/runs`
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: testKeys.runs(orgId, agentId) })
      void qc.invalidateQueries({ queryKey: testKeys.quota(orgId, agentId) })
    },
  })
}

export function useCancelTestRun(orgId: string | null, agentId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (runId: string) => {
      const res = await api.post(
        `/orgs/${orgId}/agents/${agentId}/testing/runs/${runId}/cancel`
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: testKeys.runs(orgId, agentId) })
      void qc.invalidateQueries({ queryKey: testKeys.quota(orgId, agentId) })
    },
  })
}
